<template>
  <div>
    <div
      v-for="(modalItem, index) in modals"
      :key="modalItem.id"
      :style="{ '--modal-z-index': String(100001 + index) }"
    >
      <component
        :is="getModalComponent(modalItem.id)"
        v-if="getModalComponent(modalItem.id)"
        :ref="(el) => setModalRef(index, el)"
        v-bind="modalItem.props ?? {}"
        @close="() => handleCloseModal(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { modal } from "@/events";
import * as modalRegistry from "@/components/modals";

interface ModalEventData {
  id: string;
  props?: Record<string, unknown>;
}

const modals = ref<ModalEventData[]>([]);
const modalRefs = ref<Record<number, Record<string, unknown>>>({});

/*
  Finds a component by its ID using its index in the modals array
  Ex: getModalComponent("ModalConfirm") → ModalConfirm Async Component
*/
function getModalComponent(id: string) {
  const component = (modalRegistry as Record<string, unknown>)[id];

  if (!component) {
    console.error(`Modal component not found for id: ${id}`);
    return null;
  }

  return component;
}

function setModalRef(index: number, el: Record<string, unknown> | null) {
  if (el) {
    modalRefs.value[index] = el;
  } else {
    delete modalRefs.value[index];
  }
}

function handleCloseModal(index: number) {
  delete modalRefs.value[index];
  modals.value.splice(index, 1);
}

function handleCloseByEscape(event: KeyboardEvent) {
  if (event.key === "Escape") {
    const lastIndex = modals.value.length - 1;
    const lastModal = modals.value[lastIndex];
    const componentInstance = modalRefs.value[lastIndex];
    
    if (lastModal?.props?.noCloseOnEsc === true) {
      return; 
    }
    
    if (componentInstance) {
      const instance = Array.isArray(componentInstance) ? componentInstance[0] : componentInstance;
      if (instance?.saveLoading === true || instance?.noCloseOnEsc === true) {
        return; 
      }
    }
    
    handleCloseModal(lastIndex);
  }
}

onMounted(() => {
  modal.event.on("show", (data: ModalEventData) => {
    requestAnimationFrame(() => {
      modals.value = [...modals.value, data];
    });
  });
  document.addEventListener("keydown", handleCloseByEscape);
});

onUnmounted(() => {
  modal.event.off("show");
  document.removeEventListener("keydown", handleCloseByEscape);
});

</script>
